/*!
 * Black Dashboard PRO React - v1.0.0
 * Product Page: https://www.creative-tim.com/product/black-dashboard-pro-react
 * Copyright 2019 Creative Tim (https://www.creative-tim.com)
 * Coded by Creative Tim
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */
// This must be the first line in src/index.js
import 'react-app-polyfill/ie9';

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, CardTitle, FormGroup, Row, Col, Collapse } from 'reactstrap';
import '../../../assets/react-mapbox-autocomplete/index.css';

import * as shipment_urls from '../../../urls/api-shipments/merchant';
import * as ai_urls from '../../../urls/api-ai/merchant';
import LineItems from './DirectComponents/LineItems';
import NumberOfGophrs from './DirectComponents/NumberOfGophrs';
import VehicleTypes from './DirectComponents/VehicleTypes';
import PriceChoices from './DirectComponents/PriceChoices';
import BrokerAssistPanel from './DirectComponents/BrokerAssistPanel';
import PickupInfoRegular from './DirectComponents/PickupInfoRegular';
import DropoffInfoRegular from './DirectComponents/DropoffInfoRegular';
import MerchantOrderID from './DirectComponents/MerchantOrderID';
import WightOptions from './DirectComponents/WeightOptions';
import Tools from './DirectComponents/Tools';
import { normalizeShipmentVehicleTypeForApi } from './vehicleTypeApi';

const BROKER_NOTES_HEADER = 'Gophr Broker notes:';
const LOCAL_QUOTE_RETRY_COOLDOWN_MS = 60000;
const DEFAULT_ORIENTATION_STOP_TIME_MINUTES = 30;
const MERCHANT_WEIGHT_SELECTIONS = [50, 100, 200, 300, 400, 500, 1000, 1500, 2500, 3500, 4500, 6250, 8750, 11250, 13750, 16250, 18750, 20000];
const TRAILER_VEHICLE_TYPES = new Set(['trailer', 'enclosed_trailer', 'dump_trailer', 'gooseneck_trailer', 'car_hauler']);

const hasValidTrailerDimensions = dimensions => {
    if (!dimensions || typeof dimensions !== 'object') {
        return false;
    }

    return ['length', 'width', 'height'].every(key => {
        const numericValue = Number(dimensions[key]);
        return Number.isFinite(numericValue) && numericValue > 0;
    });
};

const extractRequestErrorMessage = error => {
    const responseData = error && error.response ? error.response.data : null;

    if (typeof responseData === 'string' && responseData.trim() !== '') {
        return responseData.trim();
    }

    if (responseData && typeof responseData.message === 'string' && responseData.message.trim() !== '') {
        return responseData.message.trim();
    }

    if (responseData && typeof responseData.error === 'string' && responseData.error.trim() !== '') {
        return responseData.error.trim();
    }

    if (error && typeof error.message === 'string' && error.message.trim() !== '') {
        return error.message.trim();
    }

    return 'Something went wrong. Please refresh the page, and try again.';
};

const VEHICLE_TYPE_ALIASES = {
    car: 'car',
    suv: 'suv',
    truck: 'truck',
    pickup: 'truck',
    pickup_truck: 'truck',
    heavy_duty_truck: 'heavy_duty_truck',
    one_ton_pickup: 'heavy_duty_truck',
    sprinter_van: 'sprinter_van',
    trailer: 'trailer',
    bumper_pull: 'trailer',
    bumper_pull_trailer: 'trailer',
    box_truck: 'box_truck',
    car_hauler: 'car_hauler',
    gooseneck: 'gooseneck_trailer',
    gooseneck_trailer: 'gooseneck_trailer',
    flatbed: 'trailer',
    dry_van: '18_wheeler_dry_van',
    reefer: '18_wheeler_reefer',
    '18_wheeler_flatbed': '18_wheeler_flat_bed',
    '18_wheeler_flat_bed': '18_wheeler_flat_bed',
    '18_wheeler_dryvan': '18_wheeler_dry_van',
    '18_wheeler_dry_van': '18_wheeler_dry_van',
    '18_wheeler_reefer': '18_wheeler_reefer'
};

const normalizeBrokerVehicleType = vehicleRecommendation => {
    const normalized = (vehicleRecommendation || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');

    if (normalized.includes('18') || normalized.includes('semi') || normalized.includes('tractor')) {
        if (normalized.includes('flat') || normalized.includes('stake')) {
            return '18_wheeler_flat_bed';
        }
        if (normalized.includes('reefer')) {
            return '18_wheeler_reefer';
        }
        if (normalized.includes('dry')) {
            return '18_wheeler_dry_van';
        }
    }

    if (normalized.includes('stakebed') || normalized.includes('stake_bed')) {
        return 'trailer';
    }

    if (normalized.includes('bumper_pull') || normalized.includes('bumperpull')) {
        return 'trailer';
    }

    if (normalized.includes('flatbed') || normalized.includes('flat_bed')) {
        return normalized.includes('gooseneck') ? 'gooseneck_trailer' : 'trailer';
    }

    if (normalized.includes('straight_truck') || normalized.includes('straighttruck')) {
        return 'box_truck';
    }

    if (normalized.includes('sprinter')) {
        return 'sprinter_van';
    }

    if (normalized.includes('gooseneck')) {
        return 'gooseneck_trailer';
    }

    if (normalized.includes('box_truck') || normalized.includes('boxtruck')) {
        return 'box_truck';
    }

    if (normalized.includes('heavy_duty') || normalized.includes('heavyduty') || normalized.includes('one_ton') || normalized.includes('1_ton')) {
        return 'heavy_duty_truck';
    }

    if (normalized.includes('pickup') || normalized.includes('half_ton') || normalized.includes('1_2_ton')) {
        return 'truck';
    }

    return normalizeShipmentVehicleTypeForApi(VEHICLE_TYPE_ALIASES[normalized] || '');
};

const BROKER_TOOL_ALIASES = {
    handloading: 'ishandloading',
    handload: 'ishandloading',
    manualloading: 'ishandloading',
    dolly: 'isdolly',
    palletjack: 'ispalletjack',
    pallettruck: 'ispalletjack',
    liftgate: 'isliftgate',
    hazmat: 'ishazmat',
    twic: 'istwiccard',
    twiccard: 'istwiccard',
    weatherprotection: 'isweatherprotection',
    medical: 'ismedical',
    personalprotectiveequipment: 'isppe',
    protectiveequipment: 'isppe',
    ppe: 'isppe'
};

const normalizeBrokerToolToken = value => {
    const normalized = (value || '').toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '');

    if (!normalized) {
        return null;
    }

    return BROKER_TOOL_ALIASES[normalized] || normalized;
};

const normalizeBrokerTools = tools => {
    const normalizedTools = new Set();

    const addToken = value => {
        const token = normalizeBrokerToolToken(value);

        if (token) {
            normalizedTools.add(token);
        }
    };

    const collect = value => {
        if (value == null) {
            return;
        }

        if (Array.isArray(value)) {
            value.forEach(collect);
            return;
        }

        if (typeof value === 'object') {
            ['name', 'label', 'tool', 'type', 'equipment', 'key'].forEach(field => {
                if (value[field]) {
                    addToken(value[field]);
                }
            });

            Object.keys(value).forEach(key => {
                if (value[key] === true) {
                    addToken(key);
                }
            });

            return;
        }

        addToken(value);
    };

    collect(tools);
    return normalizedTools;
};

const parseBrokerWeight = value => {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        return Math.round(value);
    }

    if (typeof value !== 'string') {
        return null;
    }

    const matches = value.match(/\d[\d,]*(?:\.\d+)?/g);
    if (!matches || matches.length === 0) {
        return null;
    }

    const normalizedNumbers = matches
        .map(match => Number.parseFloat(match.replace(/,/g, '')))
        .filter(number => Number.isFinite(number) && number > 0);

    if (normalizedNumbers.length === 0) {
        return null;
    }

    return Math.round(Math.max(...normalizedNumbers));
};

const parseDurationMinutes = value => {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        return Math.round(value);
    }

    if (typeof value !== 'string') {
        return null;
    }

    const normalizedValue = value.trim().toLowerCase();

    if (!normalizedValue) {
        return null;
    }

    const hourMinuteMatch = normalizedValue.match(/(\d+(?:\.\d+)?)\s*(hours?|hrs?|hr|h)\s*(?:(\d+(?:\.\d+)?)\s*(minutes?|mins?|min|m))?/i);

    if (hourMinuteMatch) {
        const hours = Number.parseFloat(hourMinuteMatch[1] || '0');
        const minutes = Number.parseFloat(hourMinuteMatch[3] || '0');
        const totalMinutes = hours * 60 + minutes;

        return Number.isFinite(totalMinutes) && totalMinutes > 0 ? Math.round(totalMinutes) : null;
    }

    const minuteMatch = normalizedValue.match(/(\d+(?:\.\d+)?)\s*(minutes?|mins?|min|m)\b/i);

    if (minuteMatch) {
        const minutes = Number.parseFloat(minuteMatch[1]);
        return Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes) : null;
    }

    const hourOnlyMatch = normalizedValue.match(/(\d+(?:\.\d+)?)\s*(hours?|hrs?|hr|h)\b/i);

    if (hourOnlyMatch) {
        const hours = Number.parseFloat(hourOnlyMatch[1]);
        return Number.isFinite(hours) && hours > 0 ? Math.round(hours * 60) : null;
    }

    const fallbackNumberMatch = normalizedValue.match(/\d+(?:\.\d+)?/);

    if (fallbackNumberMatch) {
        const minutes = Number.parseFloat(fallbackNumberMatch[0]);
        return Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes) : null;
    }

    return null;
};

const getOrientationStopTimeMinutes = orientationNotes => {
    if (typeof orientationNotes !== 'string') {
        return null;
    }

    const normalizedNotes = orientationNotes.trim().toLowerCase();

    if (!normalizedNotes) {
        return null;
    }

    if (!/(orientation|video|training|safety)/i.test(normalizedNotes)) {
        return null;
    }

    return parseDurationMinutes(normalizedNotes) || DEFAULT_ORIENTATION_STOP_TIME_MINUTES;
};

const getBrokerStopTimeMinutes = ({ estimatedMinutes, orientationNotes }) => {
    const parsedEstimatedMinutes = parseDurationMinutes(estimatedMinutes);
    const orientationMinutes = getOrientationStopTimeMinutes(orientationNotes);
    const stopTimeCandidates = [parsedEstimatedMinutes, orientationMinutes].filter(minutes => Number.isFinite(minutes) && minutes > 0);

    if (stopTimeCandidates.length === 0) {
        return null;
    }

    return Math.max(...stopTimeCandidates);
};

const buildQuoteStops = ({ pickupInfo, dropoffInfo, brokerAssist }) => {
    const pickupStopTime = getBrokerStopTimeMinutes({
        estimatedMinutes: brokerAssist && brokerAssist.estimated_load_minutes,
        orientationNotes: brokerAssist && brokerAssist.pickup_site_orientation,
    });
    const dropoffStopTime = getBrokerStopTimeMinutes({
        estimatedMinutes: brokerAssist && brokerAssist.estimated_unload_minutes,
        orientationNotes: brokerAssist && brokerAssist.dropoff_site_orientation,
    });

    return [
        {
            stopType: 'PICK',
            stopOrder: 1,
            postalCode: pickupInfo.zip || '',
            countryCode: pickupInfo.country === 'Canada' ? 'CA' : 'US',
            ...(pickupStopTime ? { stop_time: pickupStopTime } : {})
        },
        {
            stopType: 'DROP',
            stopOrder: 2,
            postalCode: dropoffInfo.zip || '',
            countryCode: dropoffInfo.country === 'Canada' ? 'CA' : 'US',
            ...(dropoffStopTime ? { stop_time: dropoffStopTime } : {})
        }
    ];
};


const parseNullableCoordinate = value => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
};

const hasDraftLocationSeed = info =>
    Boolean(
        info &&
            (info.address ||
                info.address_1 ||
                info.first_name ||
                info.last_name ||
                info.phone ||
                info.business_name ||
                info.lat != null ||
                info.lng != null)
    );

const buildFullAddress = info => {
    if (!info) {
        return '';
    }

    if (typeof info.address === 'string' && info.address.trim() !== '') {
        return info.address;
    }

    const addressParts = [info.address_1, info.city, info.state, info.zip, info.country]
        .filter(part => typeof part === 'string' && part.trim() !== '');

    return addressParts.join(', ');
};
const normalizeBrokerWeightSelection = value => {
    if (!Number.isFinite(value) || value <= 0) {
        return null;
    }

    return MERCHANT_WEIGHT_SELECTIONS.find(selection => value <= selection) || MERCHANT_WEIGHT_SELECTIONS[MERCHANT_WEIGHT_SELECTIONS.length - 1];
};

const getBrokerVehicleRecommendation = brokerPlan =>
    brokerPlan && (
        brokerPlan.vehicleRecommendation
        || brokerPlan.vehicle_recommendation
        || brokerPlan.vehicle_type
        || brokerPlan.recommended_vehicle
        || brokerPlan.recommendedVehicle
    );

const getBrokerWeightRecommendation = brokerPlan =>
    brokerPlan && (
        brokerPlan.total_weight
        || brokerPlan.totalWeight
        || brokerPlan.estimated_weight
        || brokerPlan.estimatedWeight
        || brokerPlan.weight
    );

const getBrokerHelperCount = brokerPlan =>
    brokerPlan && (
        brokerPlan.num_of_gophrs
        || brokerPlan.numOfGophrs
        || brokerPlan.helper_count
        || brokerPlan.helperCount
        || brokerPlan.num_people
        || brokerPlan.numPeople
    );

const removeExistingBrokerBlock = value => {
    const trimmed = (value || '').trim();
    const markerIndex = trimmed.indexOf(BROKER_NOTES_HEADER);
    return markerIndex === -1 ? trimmed : trimmed.slice(0, markerIndex).trim();
};

const splitBrokerBullets = value =>
    (value || '')
        .split(/[\n;]+|(?<=[.!?])\s+/)
        .map(part => part.trim())
        .map(part => part.replace(/^[-*•\s]+/, '').replace(/[.!?]+$/, '').trim())
        .filter(Boolean);

const dedupeBullets = bullets => {
    const seen = new Set();
    return bullets.filter(bullet => {
        const normalized = bullet.toLowerCase();
        if (seen.has(normalized)) {
            return false;
        }
        seen.add(normalized);
        return true;
    });
};

const normalizeInstructionText = value =>
    String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

const titleizeBullet = bullet => (bullet ? bullet.charAt(0).toUpperCase() + bullet.slice(1) : bullet);

const getBrokerInstructionBullets = brokerAssist => {
    const toolBullets = [];
    const normalizedTools = normalizeBrokerTools(brokerAssist && brokerAssist.equipmentAndTools);

    if (normalizedTools.has('ishandloading')) toolBullets.push('Hand load required');
    if (normalizedTools.has('isdolly')) toolBullets.push('Dolly required');
    if (normalizedTools.has('ispalletjack')) toolBullets.push('Pallet jack required');
    if (normalizedTools.has('isliftgate')) toolBullets.push('Lift gate required');
    if (normalizedTools.has('isweatherprotection')) toolBullets.push('Keep load dry');
    if (normalizedTools.has('isforklift')) toolBullets.push('Forklift required');
    if (normalizedTools.has('ishazmat')) toolBullets.push('Hazmat handling required');
    if (normalizedTools.has('istwiccard') || normalizedTools.has('istwic_card')) toolBullets.push('TWIC required');
    if (normalizedTools.has('ismedical')) toolBullets.push('Medical handling required');
    if (normalizedTools.has('isppe')) toolBullets.push('PPE required');

    if (brokerAssist && brokerAssist.estimated_load_minutes) {
        toolBullets.push(`Estimated load time ${brokerAssist.estimated_load_minutes} minutes`);
    }
    if (brokerAssist && brokerAssist.estimated_unload_minutes) {
        toolBullets.push(`Estimated unload time ${brokerAssist.estimated_unload_minutes} minutes`);
    }
    if (brokerAssist && brokerAssist.schedule_reasoning) {
        toolBullets.push(brokerAssist.schedule_reasoning);
    }

    return dedupeBullets([
        ...toolBullets,
        ...splitBrokerBullets(brokerAssist && brokerAssist.special_handling_notes)
    ]).map(titleizeBullet);
};

const getBrokerLocationBullets = (locationKey, brokerAssist) => {
    const location = brokerAssist && brokerAssist[locationKey] ? brokerAssist[locationKey] : null;
    const label = locationKey === 'pickup_location' ? 'Pickup' : 'Dropoff';

    return dedupeBullets([
        ...splitBrokerBullets(location && location.special_instructions),
        ...splitBrokerBullets(location && location.access_notes),
        ...splitBrokerBullets(location && location.operational_notes),
        ...splitBrokerBullets(location && location.site_notes),
        ...splitBrokerBullets(location && location.driver_notes),
        ...splitBrokerBullets(brokerAssist && brokerAssist[`${locationKey === 'pickup_location' ? 'pickup' : 'dropoff'}_instructions`]),
        ...splitBrokerBullets(brokerAssist && brokerAssist[`${locationKey === 'pickup_location' ? 'pickup' : 'dropoff'}_site_orientation`]).map(bullet => `${label} site: ${bullet}`),
        ...splitBrokerBullets(brokerAssist && brokerAssist[`${locationKey === 'pickup_location' ? 'pickup' : 'dropoff'}_hours`]).map(bullet => `${label} hours: ${bullet}`),
    ]).map(titleizeBullet);
};

const mergeBrokerInstructions = ({ existing, locationSpecific, brokerAssist }) => {
    const preservedInstructions = removeExistingBrokerBlock(existing);
    const existingBullets = new Set(splitBrokerBullets(preservedInstructions).map(normalizeInstructionText).filter(Boolean));
    const generatedBullets = dedupeBullets([
        ...splitBrokerBullets(locationSpecific),
        ...getBrokerInstructionBullets(brokerAssist)
    ])
        .filter(bullet => {
            const normalizedBullet = normalizeInstructionText(bullet);
            return normalizedBullet && !existingBullets.has(normalizedBullet);
        })
        .map(titleizeBullet);

    if (!generatedBullets.length) {
        return preservedInstructions;
    }

    const brokerBlock = [BROKER_NOTES_HEADER, ...generatedBullets.map(bullet => `- ${bullet}`)].join('\n');
    return preservedInstructions ? `${preservedInstructions}\n\n${brokerBlock}` : brokerBlock;
};

const RegularCreation = ({
    showForm1,
    setShowForm1,
    setShowForm3,
    showForm2,
    setShowForm2,
    pickupInfo,
    setPickupInfo,
    pickupInstruction,
    setPickupInstruction,
    merchantOrderID,
    setMerchantOrderID,
    vehicleType,
    setVehicleType,
    numPeople,
    setNumPeople,
    scheduledFor,
    setScheduledFor,
    setIsExpedited,
    isScheduled,
    setIsScheduled,
    price,
    setPrice,
    isSaved,
    setIsSaved,
    setOrderStatus,
    setShipmentStatus,
    dropoffInstruction,
    setDropoffInstruction,
    storeFirstName,
    storeLastName,
    storePhone,
    storeAddress,
    storeAddress1,
    storeAddress2,
    storeCity,
    storeZip,
    storeState,
    storeCountry,
    storeName,
    storeLat,
    storeLng,
    weight,
    setWeight,
    defaultItems,
    setDefaultItems,
    dropoffInfo,
    setDropoffInfo,
    storeMarketId,
    setIsSuperExpedited,
    isRoundTrip,
    setDistance,
    setRoute,
    errorSubmit,
    handleErrorSubmit,
    notify,
    setErrorMessageDistance,
    errorMessageSubmit,
    setErrorMessageSubmit,
    storeId,
    vendorLat,
    vendorLong,
    isExpedited,
    isSuperExpedited,
    setIsForkLift,
    isForkLift,
    setIsDolly,
    isDolly,
    setIsHandLoadUnload,
    isHandLoadUnload,
    setIsHazmat,
    isHazmat,
    setIsHotBag,
    isHotBag,
    setIsCooler,
    isCooler,
    isTWICCard,
    setIsTWICCard,
    isLiftgate,
    setIsLiftgate,
    isPalletJack,
    setIsPalletJack,
    isWeatherProtection,
    setIsWeatherProtection,
    isMedical,
    setIsMedical,
    isPPE,
    setIsPPE,
    standardPriceRangeLow,
    setStandardPriceRangeLow,
    standardPriceRangeHigh,
    setStandardPriceRangeHigh,
    expeditedPriceRangeLow,
    setExpeditedPriceRangeLow,
    expeditedPriceRangeHigh,
    setExpeditedPriceRangeHigh,
    superExpeditedPriceRangeLow,
    setSuperExpeditedPriceRangeLow,
    superExpeditedPriceRangeHigh,
    setSuperExpeditedPriceRangeHigh,
    priceRangeLow,
    setPriceRangeLow,
    setPriceRangeHigh,
    setStandardQuotePayload,
    setExpeditedQuotePayload,
    setSuperExpeditedQuotePayload,
    isInvoice,
    checkInvoice,
    setHasTqlRateValue,
    setTqlRateBlob,
    isShipTomorrow,
    setIsShipTomorrow
}) => {
    var moment = require('moment');
    var tomorrow = moment().add(1, 'day');
    const doesLocationMatchStore = info => {
        if (!info) {
            return false;
        }

        const infoLat = parseNullableCoordinate(info.lat);
        const infoLng = parseNullableCoordinate(info.lng);
        const normalizedStoreLat = parseNullableCoordinate(storeLat);
        const normalizedStoreLng = parseNullableCoordinate(storeLng);

        if (infoLat != null && infoLng != null && normalizedStoreLat != null && normalizedStoreLng != null) {
            return infoLat === normalizedStoreLat && infoLng === normalizedStoreLng;
        }

        const locationAddress = String(info.address || info.address_1 || '').trim().toLowerCase();
        const normalizedStoreAddress = String(storeAddress || storeAddress1 || '').trim().toLowerCase();

        return locationAddress !== '' && normalizedStoreAddress !== '' && locationAddress === normalizedStoreAddress;
    };
    const needsDraftSeed = hasDraftLocationSeed(pickupInfo) || hasDraftLocationSeed(dropoffInfo);

    const [address, setAddress] = useState('');
    const [controlLat] = useState('30.21294');
    const [controlLong] = useState('-93.2187');
    const [, setDescription] = useState('');

    const [dropAddress1, setDropAddress1] = useState(dropoffInfo && dropoffInfo.address_1 ? dropoffInfo.address_1 : null);
    const [dropAddress2, setDropAddress2] = useState(dropoffInfo && dropoffInfo.address_2 ? dropoffInfo.address_2 : null);
    const [dropCity, setDropCity] = useState(dropoffInfo && dropoffInfo.city ? dropoffInfo.city : null);
    const [dropCountry, setDropCountry] = useState(dropoffInfo && dropoffInfo.country ? dropoffInfo.country : null);
    const [dropFirstname, setDropFirstname] = useState(dropoffInfo && dropoffInfo.first_name ? dropoffInfo.first_name : '');
    const [dropFullAddress, setDropFullAddress] = useState(buildFullAddress(dropoffInfo));
    const [dropLastname, setDropLastname] = useState(dropoffInfo && dropoffInfo.last_name ? dropoffInfo.last_name : '');
    const [dropLat, setDropLat] = useState(parseNullableCoordinate(dropoffInfo && dropoffInfo.lat));
    const [dropLng, setDropLng] = useState(parseNullableCoordinate(dropoffInfo && dropoffInfo.lng));
    const [dropPhone, setDropPhone] = useState(dropoffInfo && dropoffInfo.phone ? dropoffInfo.phone : '');
    const [dropState, setDropState] = useState(dropoffInfo && dropoffInfo.state ? dropoffInfo.state : null);
    const [dropZip, setDropZip] = useState(dropoffInfo && dropoffInfo.zip ? dropoffInfo.zip : null);

    const [dropoffBusinessName, setDropoffBusinessName] = useState(dropoffInfo && dropoffInfo.business_name ? dropoffInfo.business_name : '');

    const [errorDistance, setErrorDistance] = useState({});
    const [errors, setErrors] = useState({
        pickupFirstname: false,
        pickupLastname: false,
        pickupPhone: false,
        pickupAddress: false,
        dropoffFirstname: false,
        dropoffLastname: false,
        dropoffPhone: false,
        description: false,
        weight: false,
        gophrs: false,
        size: false,
        trailerDimensions: false,
        price: false
    });
    const [trailerDimensions, setTrailerDimensions] = useState({ length: '', width: '', height: '' });
    // Once the user manually picks a vehicle, the AI broker must not override it.
    const vehicleTypeManuallySelectedRef = useRef(false);
    const handleManualVehicleTypeChange = nextVehicleType => {
        vehicleTypeManuallySelectedRef.current = true;
        setVehicleType(nextVehicleType);
    };

    const [expeditedPrice, setExpeditedPrice] = useState('');
    const [superExpeditedPrice, setSuperExpeditedPrice] = useState('');
    const [isDropoffStore, setIsDropoffStore] = useState(doesLocationMatchStore(dropoffInfo));
    const [isPickupStore, setIsPickupStore] = useState(needsDraftSeed ? doesLocationMatchStore(pickupInfo) : true);

    const [pickAddress1, setPickAddress1] = useState(pickupInfo && pickupInfo.address_1 ? pickupInfo.address_1 : null);
    const [pickAddress2, setPickAddress2] = useState(pickupInfo && pickupInfo.address_2 ? pickupInfo.address_2 : null);
    const [pickCity, setPickCity] = useState(pickupInfo && pickupInfo.city ? pickupInfo.city : null);
    const [pickCountry, setPickCountry] = useState(pickupInfo && pickupInfo.country ? pickupInfo.country : null);
    const [pickFirstname, setPickFirstname] = useState(pickupInfo && pickupInfo.first_name ? pickupInfo.first_name : '');
    const [pickFullAddress, setPickFullAddress] = useState(buildFullAddress(pickupInfo));
    const [pickLastname, setPickLastname] = useState(pickupInfo && pickupInfo.last_name ? pickupInfo.last_name : '');
    const [pickLat, setPickLat] = useState(parseNullableCoordinate(pickupInfo && pickupInfo.lat));
    const [pickLng, setPickLng] = useState(parseNullableCoordinate(pickupInfo && pickupInfo.lng));
    const [pickPhone, setPickPhone] = useState(pickupInfo && pickupInfo.phone ? pickupInfo.phone : '');
    const [pickState, setPickState] = useState(pickupInfo && pickupInfo.state ? pickupInfo.state : null);
    const [pickZip, setPickZip] = useState(pickupInfo && pickupInfo.zip ? pickupInfo.zip : null);
    const [pickBusinessName, setPickBusinessName] = useState(pickupInfo && pickupInfo.business_name ? pickupInfo.business_name : '');

    const [scheduledDate, setScheduledDate] = useState(tomorrow);
    const [scheduledTime, setScheduledTime] = useState(null);

    const [showForm, setShowForm] = useState(true);
    const [standardPrice, setStandardPrice] = useState('');
    const [defaultItemsError, setDefaultItemsError] = useState([false]);
    const [twoStopsError, setTwoStopsError] = useState(false);

    //! autofill customer
    const [pickupCustomerSuggestions, setPickupCustomerSuggestions] = useState([]);
    const [dropCustomerSuggestions, setDropCustomerSuggestions] = useState([]);
    const [isPickupAutoFilled, setIsPickupAutoFilled] = useState(false);
    const [isDropOffAutoFilled, setIsDropOffAutoFilled] = useState(false);
    const [isPickupPinnedLocation, setIsPickupPinnedLocation] = useState(false);
    const [isDropoffPinnedLocation, setIsDropoffPinnedLocation] = useState(false);
    const [hasSeededDraftFields, setHasSeededDraftFields] = useState(!needsDraftSeed);

    const [isProceeded, setIsProceeded] = useState(false);
    const [brokerAssist, setBrokerAssist] = useState(null);
    const [brokerLoading, setBrokerLoading] = useState(false);
    const [brokerError, setBrokerError] = useState('');
    const [dynamicPricingInfo, setDynamicPricingInfo] = useState(null);

    const quoteScheduledFor = isScheduled && scheduledFor ? moment(scheduledFor).toISOString() : null;
    const quoteScheduledForTimeZone = isScheduled && scheduledFor ? Intl.DateTimeFormat().resolvedOptions().timeZone : null;

    // Derive dynamic pricing service level from existing form flags
    const derivedServiceLevel = (() => {
        if (isExpedited || isSuperExpedited) return 'EXPEDITED';
        if (isScheduled && scheduledFor) {
            const pickupMs = moment(scheduledFor).valueOf();
            const nowMs = Date.now();
            const hoursAhead = (pickupMs - nowMs) / (1000 * 60 * 60);
            const tomorrow = moment().add(1, 'day');
            const isTomorrowDate = moment(scheduledFor).isSame(tomorrow, 'day');
            if (hoursAhead >= 24) return isTomorrowDate ? 'SHIP_TOMORROW' : 'SCHEDULED';
            return 'SAME_DAY';
        }
        return 'SAME_DAY';
    })();

    const MAPBOX_ENDPOINT = 'https://api.mapbox.com/directions/v5/mapbox/driving/';
    const MAPBOX_TOKEN = 'pk.eyJ1IjoiZ29waHItZW5naW5lZXJpbmciLCJhIjoiY2syYzd6NjdrMmo3azNtcGU1cXZzMTh1MSJ9.cHMxwcG_MoQk-l4ZO3bdEA';
    const ACCESS_TOKEN = 'Bearer ' + window.localStorage.getItem('access_token');

    const buildBrokerPrompt = () => {
        const itemLines = (defaultItems || [])
            .map(item => `- ${item.quantity || 0} x ${item.detail || 'item'}`)
            .join('\n');

        const tools = [
            isForkLift ? 'fork lift' : null,
            isDolly ? 'dolly' : null,
            isHandLoadUnload ? 'hand loading' : null,
            isHazmat ? 'hazmat' : null,
            isTWICCard ? 'TWIC' : null,
            isLiftgate ? 'liftgate' : null,
            isPalletJack ? 'pallet jack' : null,
            isWeatherProtection ? 'weather protection' : null,
            isMedical ? 'medical handling' : null,
            isPPE ? 'ppe required' : null
        ]
            .filter(Boolean)
            .join(', ');

        return `Assess this shipment for Gophr Broker.
    Determine whether this should move via Gophr direct, LTL, or FTL. Return the recommended vehicle, any equipment needed, the likely shipment mode, quote options, estimated load and unload times, any visible pickup/dropoff orientation constraints, likely shipping/receiving hours when visible, and whether this should move same day, expedited, or next day.

Pickup:
    ${pickupInfo.address || storeAddress || ''}
    ${pickupInfo.business_name ? `Business: ${pickupInfo.business_name}` : ''}
    ${pickupInstruction ? `Instructions: ${pickupInstruction}` : ''}

Dropoff:
    ${dropoffInfo.address || ''}
    ${dropoffInfo.business_name ? `Business: ${dropoffInfo.business_name}` : ''}
    ${dropoffInstruction ? `Instructions: ${dropoffInstruction}` : ''}

Items:
${itemLines || '- No items provided'}

Current selection:
- Vehicle: ${vehicleType || 'not selected'}
- Helpers: ${numPeople || 1}
- Total weight: ${weight || 0} lb
- Tools: ${tools || 'none selected'}
- Requested schedule: ${isScheduled && scheduledFor ? scheduledFor.format ? scheduledFor.format() : scheduledFor : 'not scheduled yet'}
- Order created at local time: ${moment().format('YYYY-MM-DD HH:mm')}`;
    };

    const applyBrokerAssistToForm = brokerPlan => {
        if (!brokerPlan || typeof brokerPlan !== 'object') {
            return;
        }

        const normalizedTools = normalizeBrokerTools(brokerPlan.equipmentAndTools);
        const nextVehicleType = normalizeBrokerVehicleType(getBrokerVehicleRecommendation(brokerPlan));
        const nextWeight = normalizeBrokerWeightSelection(parseBrokerWeight(getBrokerWeightRecommendation(brokerPlan)));
        const nextHelperCount = parseInt(getBrokerHelperCount(brokerPlan), 10);

        if (Number.isFinite(nextWeight) && nextWeight > 0) {
            setWeight(nextWeight);
        }

        if (!Number.isNaN(nextHelperCount) && nextHelperCount > 0) {
            setNumPeople(nextHelperCount);
        }

        if (nextVehicleType && !vehicleTypeManuallySelectedRef.current) {
            setVehicleType(nextVehicleType);
        }

        setIsForkLift(normalizedTools.has('isforklift'));
        setIsDolly(normalizedTools.has('isdolly'));
        setIsHandLoadUnload(normalizedTools.has('ishandloading'));
        setIsHazmat(normalizedTools.has('ishazmat'));
        setIsTWICCard(normalizedTools.has('istwiccard') || normalizedTools.has('istwic_card'));
        setIsLiftgate(normalizedTools.has('isliftgate'));
        setIsPalletJack(normalizedTools.has('ispalletjack'));
        setIsWeatherProtection(normalizedTools.has('isweatherprotection'));
        setIsMedical(normalizedTools.has('ismedical'));
        setIsPPE(normalizedTools.has('isppe'));

        setPickupInstruction(
            mergeBrokerInstructions({
                existing: pickupInstruction,
                locationSpecific: getBrokerLocationBullets('pickup_location', brokerPlan).join('\n'),
                brokerAssist: brokerPlan
            })
        );

        setDropoffInstruction(
            mergeBrokerInstructions({
                existing: dropoffInstruction,
                locationSpecific: getBrokerLocationBullets('dropoff_location', brokerPlan).join('\n'),
                brokerAssist: brokerPlan
            })
        );
    };

    const handleBrokerAssist = () => {
        if (brokerLoading) {
            return;
        }

        setBrokerLoading(true);
        setBrokerError('');

        axios({
            method: 'post',
            url: ai_urls.textOnlyBrokerChat,
            withCredentials: true,
            headers: { 'content-type': 'application/json', Authorization: ACCESS_TOKEN },
            data: {
                text: buildBrokerPrompt(),
                marketId: storeMarketId
            }
        })
            .then(response => {
                setBrokerAssist(response.data);
                applyBrokerAssistToForm(response.data);
            })
            .catch(() => {
                setBrokerAssist(null);
                setBrokerError('Gophr Broker could not build a plan right now. Please try again.');
            })
            .finally(() => {
                setBrokerLoading(false);
            });
    };

    useEffect(() => {
        if (standardPrice) {
            if (isExpedited) {
                setPrice(expeditedPrice);
            } else if (isSuperExpedited) {
                setPrice(superExpeditedPrice);
            } else {
                setPrice(standardPrice);
            }
        }
    }, [standardPrice]);

    useEffect(() => {
        if (!TRAILER_VEHICLE_TYPES.has(vehicleType)) {
            if (errors.trailerDimensions) {
                setErrors(previousErrors => ({ ...previousErrors, trailerDimensions: false }));
            }
            return;
        }

        if (hasValidTrailerDimensions(trailerDimensions) && errors.trailerDimensions) {
            setErrors(previousErrors => ({ ...previousErrors, trailerDimensions: false }));
        }
    }, [vehicleType, trailerDimensions, errors.trailerDimensions]);

    useEffect(() => {
        if (standardPriceRangeLow && standardPriceRangeHigh) {
            if (isExpedited) {
                setPriceRangeLow(expeditedPriceRangeLow);
                setPriceRangeHigh(expeditedPriceRangeHigh);
            } else if (isSuperExpedited) {
                setPriceRangeLow(superExpeditedPriceRangeLow);
                setPriceRangeHigh(superExpeditedPriceRangeHigh);
            } else {
                setPriceRangeLow(standardPriceRangeLow);
                setPriceRangeHigh(standardPriceRangeHigh);
            }
        }
    }, [standardPriceRangeLow, standardPriceRangeHigh, isExpedited, isSuperExpedited]);

    useEffect(() => {
        if (hasSeededDraftFields || !needsDraftSeed) {
            return;
        }

        setIsPickupStore(doesLocationMatchStore(pickupInfo));
        setIsDropoffStore(doesLocationMatchStore(dropoffInfo));

        setPickFirstname(pickupInfo && pickupInfo.first_name ? pickupInfo.first_name : '');
        setPickLastname(pickupInfo && pickupInfo.last_name ? pickupInfo.last_name : '');
        setPickPhone(pickupInfo && pickupInfo.phone ? pickupInfo.phone : '');
        setPickFullAddress(buildFullAddress(pickupInfo));
        setPickAddress1(pickupInfo && pickupInfo.address_1 ? pickupInfo.address_1 : null);
        setPickAddress2(pickupInfo && pickupInfo.address_2 ? pickupInfo.address_2 : null);
        setPickCity(pickupInfo && pickupInfo.city ? pickupInfo.city : null);
        setPickState(pickupInfo && pickupInfo.state ? pickupInfo.state : null);
        setPickZip(pickupInfo && pickupInfo.zip ? pickupInfo.zip : null);
        setPickCountry(pickupInfo && pickupInfo.country ? pickupInfo.country : null);
        setPickLat(parseNullableCoordinate(pickupInfo && pickupInfo.lat));
        setPickLng(parseNullableCoordinate(pickupInfo && pickupInfo.lng));
        setPickBusinessName(pickupInfo && pickupInfo.business_name ? pickupInfo.business_name : '');

        setDropFirstname(dropoffInfo && dropoffInfo.first_name ? dropoffInfo.first_name : '');
        setDropLastname(dropoffInfo && dropoffInfo.last_name ? dropoffInfo.last_name : '');
        setDropPhone(dropoffInfo && dropoffInfo.phone ? dropoffInfo.phone : '');
        setDropFullAddress(buildFullAddress(dropoffInfo));
        setDropAddress1(dropoffInfo && dropoffInfo.address_1 ? dropoffInfo.address_1 : null);
        setDropAddress2(dropoffInfo && dropoffInfo.address_2 ? dropoffInfo.address_2 : null);
        setDropCity(dropoffInfo && dropoffInfo.city ? dropoffInfo.city : null);
        setDropState(dropoffInfo && dropoffInfo.state ? dropoffInfo.state : null);
        setDropZip(dropoffInfo && dropoffInfo.zip ? dropoffInfo.zip : null);
        setDropCountry(dropoffInfo && dropoffInfo.country ? dropoffInfo.country : null);
        setDropLat(parseNullableCoordinate(dropoffInfo && dropoffInfo.lat));
        setDropLng(parseNullableCoordinate(dropoffInfo && dropoffInfo.lng));
        setDropoffBusinessName(dropoffInfo && dropoffInfo.business_name ? dropoffInfo.business_name : '');

        setHasSeededDraftFields(true);
    }, [
        hasSeededDraftFields,
        needsDraftSeed,
        pickupInfo,
        dropoffInfo,
        storeAddress,
        storeAddress1,
        storeLat,
        storeLng,
    ]);

    useEffect(() => {
        if (!hasSeededDraftFields && needsDraftSeed) {
            return;
        }

        let storeInfo = {
            name: storeFirstName + ' ' + storeLastName,
            first_name: storeFirstName,
            last_name: storeLastName,
            phone: storePhone,
            address: storeAddress,
            address_1: storeAddress1,
            address_2: storeAddress2,
            city: storeCity,
            zip: storeZip,
            state: storeState,
            country: storeCountry,
            business_name: storeName,
            lat: parseFloat(storeLat),
            lng: parseFloat(storeLng)
        };
        let dropInfo = {
            name: dropFirstname + ' ' + dropLastname,
            first_name: dropFirstname,
            last_name: dropLastname,
            phone: dropPhone,
            address: dropFullAddress,
            address_1: dropAddress1,
            address_2: dropAddress2,
            city: dropCity,
            zip: dropZip,
            state: dropState,
            country: dropCountry,
            business_name: dropoffBusinessName,
            lat: parseFloat(dropLat),
            lng: parseFloat(dropLng)
        };
        let pickInfo = {
            name: pickFirstname + ' ' + pickLastname,
            first_name: pickFirstname,
            last_name: pickLastname,
            phone: pickPhone,
            address: pickFullAddress,
            address_1: pickAddress1,
            address_2: pickAddress2,
            city: pickCity,
            zip: pickZip,
            state: pickState,
            country: pickCountry,
            business_name: pickBusinessName,
            lat: parseFloat(pickLat),
            lng: parseFloat(pickLng)
        };

        if (isPickupStore) {
            setPickupInfo(storeInfo);
            setDropoffInfo(dropInfo);
        } else if (isDropoffStore) {
            setDropoffInfo(storeInfo);
            setPickupInfo(pickInfo);
        } else {
            setPickupInfo(pickInfo);
            setDropoffInfo(dropInfo);
        }
    }, [
        pickLat,
        pickLng,
        dropLat,
        dropLng,
        storeLat,
        storeLng,
        isPickupStore,
        isDropoffStore,
        pickFirstname,
        pickLastname,
        pickPhone,
        pickBusinessName,
        dropPhone,
        dropFirstname,
        dropLastname,
        dropoffBusinessName,
        pickFullAddress,
        pickAddress1,
        pickAddress2,
        pickCity,
        pickState,
        pickZip,
        pickCountry,
        dropFullAddress,
        dropAddress1,
        dropAddress2,
        dropCity,
        dropState,
        dropZip,
        dropCountry,
        storeFirstName,
        storeLastName,
        storePhone,
        storeAddress,
        storeAddress1,
        storeAddress2,
        storeCity,
        storeZip,
        storeState,
        storeCountry,
        storeName,
        hasSeededDraftFields,
        needsDraftSeed
    ]);

    useEffect(() => {
        if (isScheduled && scheduledTime && scheduledDate) {
            setScheduledFor(
                moment(
                    moment(scheduledDate)
                        .startOf('day')
                        .add(scheduledTime, 'hours')
                )
            );
            setIsProceeded(true);
        } else if (isScheduled && (!scheduledTime || !scheduledDate)) {
            setIsProceeded(false);
        } else if (!isScheduled) {
            setIsProceeded(true);
            setScheduledFor(null);
        }
    }, [isScheduled, scheduledDate, scheduledTime]);

    const handleErrorDistance = error => {
        setErrorDistance(error);
    };

    const handleForm = (show, show2) => {
        setShowForm1(show);
        setShowForm2(show2);
    };

    const getApplicableQuotePayload = response => {
        const payload = response && response.data && response.data.payload ? response.data.payload : null;

        if (!payload) {
            return null;
        }

        if (payload.hasOwnProperty('shipping_fee_range')) {
            return payload;
        }

        if (payload.hasOwnProperty('shipping_fee') && Number(payload.shipping_fee) > 0) {
            return payload;
        }

        return null;
    };

    const ensureApplicableQuoteResponse = response => {
        if (getApplicableQuotePayload(response)) {
            return response;
        }

        throw new Error('Quote response did not include a usable shipping fee.');
    };

    const applyQuoteResponse = (response, setRangeLow, setRangeHigh, setQuotePrice, options = {}) => {
        const payload = getApplicableQuotePayload(response);

        if (!payload) {
            return false;
        }

        if (payload.hasOwnProperty('shipping_fee_range')) {
            setRangeLow(payload.shipping_fee_range.min);
            setRangeHigh(payload.shipping_fee_range.max);
            setQuotePrice(payload.shipping_fee);
            if (options.setQuotePayload) {
                options.setQuotePayload(payload);
            }
            return true;
        }

        if (payload.shipping_fee) {
            if (options.markTqlRate) {
                setHasTqlRateValue(true);
                setTqlRateBlob(response.data);
            }
            setRangeLow(0);
            setRangeHigh(0);
            setQuotePrice(payload.shipping_fee);
            if (options.setQuotePayload) {
                options.setQuotePayload(payload);
            }
            return true;
        }

        return false;
    };

    const requestQuoteWithFallback = quoteData => {
        const requestConfig = {
            method: 'post',
            withCredentials: true,
            headers: { 'content-type': 'application/json', Authorization: ACCESS_TOKEN },
            data: quoteData,
        };
        const now = Date.now();
        const canTryLocalQuote = !requestQuoteWithFallback.localRetryAt || requestQuoteWithFallback.localRetryAt <= now;

        const requestHostedLegacyQuote = () =>
            axios({
                ...requestConfig,
                url: shipment_urls.toHostedShipmentUrl(shipment_urls.getShipmentQuote),
            }).then(ensureApplicableQuoteResponse);

        const requestHostedWithRateQuote = () =>
            axios({
                ...requestConfig,
                url: shipment_urls.toHostedShipmentUrl(shipment_urls.getShipmentQuoteWithRate),
            }).then(ensureApplicableQuoteResponse);

        if (!canTryLocalQuote) {
            return requestHostedWithRateQuote().catch(() => requestHostedLegacyQuote());
        }

        return axios({
            ...requestConfig,
            url: shipment_urls.getShipmentQuoteWithRate,
        }).then(ensureApplicableQuoteResponse).catch(error => {
            const status = error && error.response ? error.response.status : null;

            if (status === null || status === 404 || status >= 500) {
                requestQuoteWithFallback.localRetryAt = Date.now() + LOCAL_QUOTE_RETRY_COOLDOWN_MS;
            }

            if (status && status < 500 && status !== 404) {
                throw error;
            }

            return requestHostedWithRateQuote().catch(hostedError => {
                return requestHostedLegacyQuote().catch(legacyHostedError => {
                    const hostedStatus = hostedError && hostedError.response ? hostedError.response.status : null;

                    if (hostedStatus && hostedStatus < 500 && hostedStatus !== 404) {
                        throw hostedError;
                    }

                    throw legacyHostedError;
                });
            });
        });
    };

    const handlePickupSelect = (result, lat, lng) => {
        if (result && typeof result === 'object') {
            setIsPickupPinnedLocation(true);
            setPickFullAddress(result.formattedAddress || result.address1 || '');
            setPickAddress1(result.address1 || result.formattedAddress || '');
            setPickAddress2(result.address2 || '');
            setPickCity(result.city || '');
            setPickState(result.state || '');
            setPickZip(result.zip || '');
            setPickCountry(result.countryCode || result.country || '');
            setPickLat(result.lat);
            setPickLng(result.lng);
            return;
        }

        setIsPickupPinnedLocation(false);

        var split = result.split(',');
        var stateZip = split[2].split(' ');
        var state = null;
        //! Mapbox returns full name of the state
        // for (var i = 0; i < states.length; i++) {
        //   if (states[i].label === stateZip[1]) {
        //     state = states[i].val;
        //     break;
        //   }
        // }
        //! Google returns abbreviated name of the state
        state = stateZip[1];
        setPickFullAddress(result);
        setPickAddress1(split[0]);
        setPickCity(split[1]);
        setPickState(state);
        setPickZip(stateZip[2]);
        setPickCountry('US');
        setPickLat(lat);
        setPickLng(lng);
    };

    const handleDropoffSelect = (result, lat, lng) => {
        if (result && typeof result === 'object') {
            setIsDropoffPinnedLocation(true);
            setDropFullAddress(result.formattedAddress || result.address1 || '');
            setDropAddress1(result.address1 || result.formattedAddress || '');
            setDropAddress2(result.address2 || '');
            setDropCity(result.city || '');
            setDropState(result.state || '');
            setDropZip(result.zip || '');
            setDropCountry(result.countryCode || result.country || '');
            setDropLat(result.lat);
            setDropLng(result.lng);
            return;
        }

        setIsDropoffPinnedLocation(false);

        var split = result.split(',');
        var stateZip = split[2].split(' ');
        var state = null;
        //! Mapbox returns full name of the state
        // for (var i = 0; i < states.length; i++) {
        //   if (states[i].label === stateZip[1]) {
        //     state = states[i].val;
        //     break;
        //   }
        // }
        //! Google returns abbreviated name of the state
        state = stateZip[1];
        setDropFullAddress(result);
        setDropAddress1(split[0]);
        setDropCity(split[1]);
        setDropState(state);
        setDropZip(stateZip[2]);
        setDropCountry('US');
        setDropLat(lat);
        setDropLng(lng);
    };

    const handleWeight = event => {
        setWeight(Number(event.target.value));
    };

    const handleSubmit = () => {
        // Yaboi has had a few and this is how he's error checking...big yikes

        const checkItemsError = (items, itemsError, setItemsError) => {
            let ItemsCheck = false;
            let itemsErrorArray = [...itemsError];
            items.forEach((pi, index) => {
                if (pi.detail === '' || pi.detail === null) {
                    itemsErrorArray[index] = true;
                    ItemsCheck = true;
                } else {
                    itemsErrorArray[index] = false;
                }
            });
            setItemsError(itemsErrorArray);
            return ItemsCheck;
        };

        let updatedErrors = errors;

        if (!isPickupStore) {
            if (pickFirstname) {
                updatedErrors.pickupFirstname = false;
            } else {
                updatedErrors.pickupFirstname = true;
            }
            if (pickLastname) {
                updatedErrors.pickupLastname = false;
            } else {
                updatedErrors.pickupLastname = true;
            }
            if (validatePhone(pickPhone)) {
                updatedErrors.pickupPhone = false;
            } else {
                updatedErrors.pickupPhone = true;
            }
            if (pickLat && pickLng) {
                updatedErrors.pickupAddress = false;
            } else {
                updatedErrors.pickupAddress = true;
            }
        } else {
            updatedErrors.pickupFirstname = false;
            updatedErrors.pickupLastname = false;
            updatedErrors.pickupPhone = false;
            updatedErrors.pickupAddress = false;
        }
        if (!isDropoffStore) {
            if (dropFirstname) {
                updatedErrors.dropoffFirstname = false;
            } else {
                updatedErrors.dropoffFirstname = true;
            }
            if (dropLastname) {
                updatedErrors.dropoffLastname = false;
            } else {
                updatedErrors.dropoffLastname = true;
            }
            if (validatePhone(dropPhone)) {
                updatedErrors.dropoffPhone = false;
            } else {
                updatedErrors.dropoffPhone = true;
            }
            if (dropLat && dropLng) {
                updatedErrors.dropoffAddress = false;
            } else {
                updatedErrors.dropoffAddress = true;
            }
        } else {
            updatedErrors.dropoffFirstname = false;
            updatedErrors.dropoffLastname = false;
            updatedErrors.dropoffPhone = false;
            updatedErrors.dropoffAddress = false;
        }

        if (weight === null || weight === '') {
            updatedErrors.weight = true;
        } else {
            updatedErrors.weight = false;
        }
        if (numPeople === null || numPeople === '') {
            updatedErrors.gophrs = true;
        } else {
            updatedErrors.gophrs = false;
        }
        if (vehicleType === null || vehicleType === '') {
            updatedErrors.size = true;
        } else {
            updatedErrors.size = false;
        }
        if (TRAILER_VEHICLE_TYPES.has(vehicleType) && !hasValidTrailerDimensions(trailerDimensions)) {
            updatedErrors.trailerDimensions = true;
        } else {
            updatedErrors.trailerDimensions = false;
        }
        if (price || priceRangeLow) {
            updatedErrors.price = false;
        } else {
            updatedErrors.price = true;
        }

        setErrors(updatedErrors);

        let invoiceCheck = false;
        if (isInvoice) {
            invoiceCheck = checkInvoice();
        }
        let regularCheck = Object.values(errors).includes(true);
        let defaultItemsCheck = checkItemsError(defaultItems, defaultItemsError, setDefaultItemsError);
        if (!regularCheck && !defaultItemsCheck && !invoiceCheck) {
            setTwoStopsError(false);
            // ! open confirm form
            handleForm(false, false);
            setShowForm3(true);
        } else {
            setTwoStopsError(true);
        }
    };

    const validatePhone = phone => {
        var phoneRex = /((?:\+|00)[17](?: |-)?|(?:\+|00)[1-9]\d{0,2}(?: |-)?|(?:\+|00)1-\d{3}(?: |-)?)?(0\d|\([0-9]{3}\)|[1-9]{0,3})(?:((?: |-)[0-9]{2}){4}|((?:[0-9]{2}){4})|((?: |-)[0-9]{3}(?: |-)[0-9]{4})|([0-9]{10}))/;

        if (phone.replace(/[^0-9]/g, '').length > 9 && phone.replace(/[^0-9]/g, '').length < 14) {
            return phoneRex.test(String(phone));
        }
    };

    // Fully resets the component
    const reset = () => {
        let radWeight = document.getElementsByName('exampleRadio1');
        let radNumPeople = document.getElementsByName('exampleRadio2');
        let radIsExpedited = document.getElementsByName('exampleRadio3');
        let radScheduleTime = document.getElementsByName('exampleRadio4');
        let scheduleCheck = document.getElementById('schedule-check');

        handleForm(true, false);

        setPickupInfo({});
        setDropoffInfo({});
        setAddress('');
        setDescription('');
        setMerchantOrderID('');
        setDropoffInstruction('');
        setDropAddress1(null);
        setDropAddress2(null);
        setDropCity(null);
        setDropCountry(null);
        setIsDropoffPinnedLocation(false);
        setDropFirstname('');
        setDropFullAddress('');
        setDropLastname('');
        setDropLat(null);
        setDropLng(null);
        setDropPhone('');
        setDropState(null);
        setDropZip(null);
        setErrors({
            pickupFirstname: false,
            pickupLastname: false,
            pickupPhone: false,
            pickupAddress: false,
            dropoffFirstname: false,
            dropoffLastname: false,
            dropoffPhone: false,
            description: false,
            weight: false,
            gophrs: false,
            size: false,
            trailerDimensions: false,
            price: false
        });

        setExpeditedPrice(null);
        setIsExpedited(false);
        setIsSaved(false);
        setIsScheduled(false);
        setNumPeople(null);
        setOrderStatus('awaiting_driver');
        setPickFullAddress('');
        setPickAddress1(null);
        setPickAddress2(null);
        setPickCity(null);
        setPickCountry(null);
        setIsPickupPinnedLocation(false);
        setPickFirstname('');
        setPickLastname('');
        setPickLat(null);
        setPickLng(null);
        setPickPhone('');
        setPickState(null);
        setPickZip(null);
        setPickupInstruction('');

        setScheduledFor(null);
        setShipmentStatus('shipment_ready_for_driver');
        setShowForm3(false);
        setStandardPrice(null);
        setVehicleType('');
        setWeight('');
        setTrailerDimensions({ length: '', width: '', height: '' });
        vehicleTypeManuallySelectedRef.current = false;

        setIsPickupStore(true);
        setIsDropoffStore(false);
        handleErrorDistance({ errorMessage: '', errorCode: 0 });
        handleErrorSubmit({ errorMessage: '', errorCode: 0 });

        // Clear the radio bubbles/checkboxes
        radWeight.forEach(element => (element.checked = false));
        radNumPeople.forEach(element => (element.checked = false));
        radIsExpedited.forEach(element => (element.checked = false));
        radScheduleTime.forEach(element => (element.checked = false));
        scheduleCheck.checked = false;

        // Clear the address input
        document.getElementById('mapbox-autocomplete').value = '';
        // ! need a way to clear mapbox
        document.getElementsByClassName('react-mapbox-ac-input')[0].value = '';

        //reset line items
        setDefaultItems([{ quantity: 1, detail: '' }]);
    };

    const usePrevious = value => {
        const ref = useRef();
        useEffect(() => {
            ref.current = value;
        });
        return ref.current;
    };
    const previousNumPeople = usePrevious(numPeople);
    const previousVehicleType = usePrevious(vehicleType);
    const previousWeight = usePrevious(weight);
    const previousDrop = usePrevious(dropoffInfo);
    const previousPick = usePrevious(pickupInfo);

    const previousRoundTrip = usePrevious(isRoundTrip);

    const previousDolly = usePrevious(isDolly);
    const previousHazmat = usePrevious(isHazmat);
    const previousTWICCard = usePrevious(isTWICCard);
    const previousLiftgate = usePrevious(isLiftgate);
    const previousPalletJack = usePrevious(isPalletJack);
    const previousHandLoad = usePrevious(isHandLoadUnload);
    const previousWeatherProtection = usePrevious(isWeatherProtection);
    const previousMedical = usePrevious(isMedical);
    const previousPPE = usePrevious(isPPE);

    useEffect(() => {
        if (isSaved) {
            setShipmentStatus('shipment_saved');
            setOrderStatus('order_saved');
        } else {
            setShipmentStatus('shipment_ready_for_driver');
            setOrderStatus('awaiting_driver');
        }

        setShowForm(showForm);
        setErrorMessageSubmit(errorSubmit);
        setErrorMessageDistance(errorDistance);

        if (
            previousDrop !== dropoffInfo ||
            previousPick !== pickupInfo ||
            previousNumPeople !== numPeople ||
            previousVehicleType !== vehicleType ||
            previousWeight !== weight ||
            previousRoundTrip !== isRoundTrip ||
            previousDolly !== isDolly ||
            previousHazmat !== isHazmat ||
            previousTWICCard !== isTWICCard ||
            previousLiftgate !== isLiftgate ||
            previousPalletJack !== isPalletJack ||
            previousHandLoad !== isHandLoadUnload ||
            previousWeatherProtection !== isWeatherProtection ||
            previousMedical !== isMedical ||
            previousPPE !== isPPE
        ) {
            // Check if form entries are valid
            let end = dropoffInfo.lng + ',' + dropoffInfo.lat;
            let start = pickupInfo.lng + ',' + pickupInfo.lat;
            //! This distance check does not make sense somehow, did not cover all cases even for 2 stops
            let requestEndpointDistanceCheck = MAPBOX_ENDPOINT + controlLong + ',' + controlLat + ';' + end + '?geometries=geojson&access_token=' + MAPBOX_TOKEN;
            let requestEndpointRegular = MAPBOX_ENDPOINT + start + ';' + end + '?alternatives=true&geometries=geojson&access_token=' + MAPBOX_TOKEN;

            if (
                dropoffInfo.lat &&
                dropoffInfo.lng &&
                pickupInfo.lat &&
                pickupInfo.lng &&
                numPeople !== '' &&
                numPeople !== null &&
                vehicleType !== '' &&
                vehicleType !== null &&
                weight !== null &&
                weight !== ''
            ) {
                // Get distance from MapBox
                axios
                    .get(requestEndpointRegular)
                    .then(response => {
                        var route = response.data.routes[0];
                        for (let i = 1; i < response.data.routes.length; i++) {
                            if (response.data.routes[i].duration < route.duration) {
                                route = response.data.routes[i];
                            }
                        }
                        if (route) {
                            setDistance(route.distance / 1609.344);
                            setRoute(route);
                            let standardData = {
                                distance: route.distance / 1609.344,
                                weight: weight,
                                is_expedited: false,
                                is_super_expedited: false,
                                quoted_time: route.duration / 60 / 60,
                                num_of_gophrs: numPeople,
                                vehicle_type: normalizeShipmentVehicleTypeForApi(vehicleType),
                                contains_alcohol: false,
                                market_id: storeMarketId,
                                order_points: [[pickupInfo.lat, pickupInfo.lng], [dropoffInfo.lat, dropoffInfo.lng]],
                                num_of_drops: 1,
                                is_roundtrip: isRoundTrip,
                                is_fork_lift: isForkLift,
                                is_dolly: isDolly,
                                is_hazmat: isHazmat,
                                is_pallet_jack: isPalletJack,
                                is_twic_card: isTWICCard,
                                is_liftgate: isLiftgate,
                                is_hand_loading: isHandLoadUnload,
                                is_weather_protection: isWeatherProtection,
                                is_medical: isMedical,
                                is_personal_protective_equipment: isPPE,
                                // Additional fields for quote_with_rate endpoint
                                temperature: 20,
                                scheduled_for: quoteScheduledFor,
                                scheduled_for_timezone: quoteScheduledForTimeZone,
                                load_value: '36000',
                                stops: buildQuoteStops({ pickupInfo, dropoffInfo, brokerAssist }),
                                commodity: (defaultItems[0] && defaultItems[0].detail) || 'items',
                                do_tql_cal: false,
                                is_over_size: false,
                                drop_off_state: dropoffInfo.state || '',
                                is_market_base_quote: false,
                                service_level: derivedServiceLevel,
                            };
                            let expeditedData = {
                                ...standardData,
                                is_expedited: true
                            };
                            let superExpeditedData = {
                                ...standardData,
                                is_super_expedited: true
                            };
                            axios.get(requestEndpointDistanceCheck).then(response => {
                                let quickDistance = route.distance / 1609.344;
                                if (quickDistance > 10000) {
                                    handleErrorDistance({
                                        errorMessage:
                                            "We're sorry, but your delivery distance of " +
                                            parseInt(quickDistance) +
                                            ' miles is too far.  Please, contact a Gophr representative for more information.',
                                        errorCode: 1
                                    });
                                } else {
                                    // Use distance to get both price quotes
                                    handleErrorDistance({ errorMessage: '', errorCode: 0 });
                                    Promise.allSettled([
                                        requestQuoteWithFallback(standardData),
                                        requestQuoteWithFallback(expeditedData),
                                        requestQuoteWithFallback(superExpeditedData)
                                    ]).then(([standardResult, expeditedResult, superExpeditedResult]) => {
                                        let hasAnyQuote = false;

                                        setHasTqlRateValue(false);

                                        if (standardResult.status === 'fulfilled') {
                                            hasAnyQuote = applyQuoteResponse(
                                                standardResult.value,
                                                setStandardPriceRangeLow,
                                                setStandardPriceRangeHigh,
                                                setStandardPrice,
                                                { markTqlRate: true, setQuotePayload: setStandardQuotePayload }
                                            ) || hasAnyQuote;
                                            // Capture dynamic pricing info for display
                                            const stdPayload = standardResult.value && standardResult.value.data && standardResult.value.data.payload;
                                            if (stdPayload && stdPayload.dynamic_pricing) {
                                                setDynamicPricingInfo(stdPayload.dynamic_pricing);
                                            }
                                        }

                                        if (expeditedResult.status === 'fulfilled') {
                                            hasAnyQuote = applyQuoteResponse(
                                                expeditedResult.value,
                                                setExpeditedPriceRangeLow,
                                                setExpeditedPriceRangeHigh,
                                                setExpeditedPrice,
                                                { setQuotePayload: setExpeditedQuotePayload }
                                            ) || hasAnyQuote;
                                        }

                                        if (superExpeditedResult.status === 'fulfilled') {
                                            hasAnyQuote = applyQuoteResponse(
                                                superExpeditedResult.value,
                                                setSuperExpeditedPriceRangeLow,
                                                setSuperExpeditedPriceRangeHigh,
                                                setSuperExpeditedPrice,
                                                { setQuotePayload: setSuperExpeditedQuotePayload }
                                            ) || hasAnyQuote;
                                        }

                                        if (hasAnyQuote) {
                                            handleErrorDistance({ errorMessage: '', errorCode: 0 });
                                            handleForm(true, true);
                                            return;
                                        }

                                        const quoteErrors = [standardResult, expeditedResult, superExpeditedResult]
                                            .filter(result => result.status === 'rejected')
                                            .map(result => extractRequestErrorMessage(result.reason));
                                        const nextErrorMessage = quoteErrors.find(Boolean) || 'Something went wrong. Please refresh the page, and try again.';

                                        handleErrorDistance({ errorMessage: nextErrorMessage, errorCode: 3 });
                                        notify('tc', 'distance');
                                    });
                                }
                            });
                        }
                    })
                    .catch(error => {
                        handleErrorDistance({ errorMessage: extractRequestErrorMessage(error), errorCode: 3 });
                        notify('tc', 'distance');
                    });
            }
        }
    });

    const priceChoice = (standardPrice, expeditedPrice, superExpeditedPrice) => {
        return (
            <PriceChoices
                showForm2={showForm2}
                address={address}
                standardPrice={standardPrice}
                expeditedPrice={expeditedPrice}
                superExpeditedPrice={superExpeditedPrice}
                setIsScheduled={setIsScheduled}
                setIsExpedited={setIsExpedited}
                setIsSuperExpedited={setIsSuperExpedited}
                price={price}
                setPrice={setPrice}
                priceChoice={priceChoice}
                setIsProceeded={setIsProceeded}
                errorMessageSubmit={errorMessageSubmit}
                scheduledTime={scheduledTime}
                setScheduledTime={setScheduledTime}
                isScheduled={isScheduled}
                setScheduledDate={setScheduledDate}
                scheduledFor={scheduledFor}
                errors={errors}
                vehicleType={vehicleType}
                isProceeded={isProceeded}
                handleSubmit={handleSubmit}
                standardPriceRangeLow={standardPriceRangeLow}
                standardPriceRangeHigh={standardPriceRangeHigh}
                expeditedPriceRangeLow={expeditedPriceRangeLow}
                expeditedPriceRangeHigh={expeditedPriceRangeHigh}
                superExpeditedPriceRangeLow={superExpeditedPriceRangeLow}
                superExpeditedPriceRangeHigh={superExpeditedPriceRangeHigh}
                priceRangeLow={priceRangeLow}
                brokerAssist={brokerAssist}
                brokerLoading={brokerLoading}
                brokerError={brokerError}
                onBrokerAssist={handleBrokerAssist}
                setIsShipTomorrow={setIsShipTomorrow}
            />
        );
    };

    return (
        <div>
            <Collapse id="collapse2" isOpen={showForm1}>
                {/* <div className="rna-container">
          <NotificationAlert ref="notificationAlert" />
        </div> */}
                <CardTitle tag="h4" style={{ padding: 5, paddingTop: 10, textAlign: 'left', paddingBottom: 0 }}>
                    Pickup Information
                </CardTitle>
                <PickupInfoRegular
                    isPickupStore={isPickupStore}
                    setIsPickupStore={setIsPickupStore}
                    setIsDropoffStore={setIsDropoffStore}
                    errors={errors}
                    storeFirstName={storeFirstName}
                    pickFirstname={pickFirstname}
                    setPickFirstname={setPickFirstname}
                    pickupCustomerSuggestions={pickupCustomerSuggestions}
                    setPickupCustomerSuggestions={setPickupCustomerSuggestions}
                    storeId={storeId}
                    setPickLastname={setPickLastname}
                    setPickPhone={setPickPhone}
                    pickFullAddress={pickFullAddress}
                    setPickFullAddress={setPickFullAddress}
                    setPickupInstruction={setPickupInstruction}
                    isPickupAutoFilled={isPickupAutoFilled}
                    setIsPickupAutoFilled={setIsPickupAutoFilled}
                    setPickAddress1={setPickAddress1}
                    setPickAddress2={setPickAddress2}
                    setPickCity={setPickCity}
                    setPickState={setPickState}
                    setPickZip={setPickZip}
                    setPickCountry={setPickCountry}
                    setPickLat={setPickLat}
                    setPickLng={setPickLng}
                    setPickBusinessName={setPickBusinessName}
                    storeLastName={storeLastName}
                    pickLastname={pickLastname}
                    storePhone={storePhone}
                    pickPhone={pickPhone}
                    storeName={storeName}
                    pickBusinessName={pickBusinessName}
                    storeAddress={storeAddress}
                    handlePickupSelect={handlePickupSelect}
                    pickLat={pickLat}
                    pickLng={pickLng}
                    isPickupPinnedLocation={isPickupPinnedLocation}
                    setIsPickupPinnedLocation={setIsPickupPinnedLocation}
                    vendorLat={vendorLat}
                    vendorLong={vendorLong}
                    pickupInstruction={pickupInstruction}
                />
                <hr style={{ borderColor: '#344675', borderStyle: 'solid' }}></hr>
                <CardTitle tag="h4" style={{ padding: 5, paddingTop: 10, textAlign: 'left' }}>
                    Dropoff Information
                </CardTitle>
                <DropoffInfoRegular
                    isDropoffStore={isDropoffStore}
                    setIsDropoffStore={setIsDropoffStore}
                    setIsPickupStore={setIsPickupStore}
                    errors={errors}
                    storeFirstName={storeFirstName}
                    dropFirstname={dropFirstname}
                    setDropFirstname={setDropFirstname}
                    dropCustomerSuggestions={dropCustomerSuggestions}
                    setDropCustomerSuggestions={setDropCustomerSuggestions}
                    storeId={storeId}
                    setDropLastname={setDropLastname}
                    setDropPhone={setDropPhone}
                    dropFullAddress={dropFullAddress}
                    setDropFullAddress={setDropFullAddress}
                    setDropoffInstruction={setDropoffInstruction}
                    isDropOffAutoFilled={isDropOffAutoFilled}
                    setIsDropOffAutoFilled={setIsDropOffAutoFilled}
                    setDropAddress1={setDropAddress1}
                    setDropAddress2={setDropAddress2}
                    setDropCity={setDropCity}
                    setDropState={setDropState}
                    setDropZip={setDropZip}
                    setDropCountry={setDropCountry}
                    setDropLat={setDropLat}
                    setDropLng={setDropLng}
                    pickFirstname={pickFirstname}
                    isPickupStore={isPickupStore}
                    setDropoffBusinessName={setDropoffBusinessName}
                    storeLastName={storeLastName}
                    dropLastname={dropLastname}
                    storePhone={storePhone}
                    dropPhone={dropPhone}
                    storeName={storeName}
                    dropoffBusinessName={dropoffBusinessName}
                    storeAddress={storeAddress}
                    handleDropoffSelect={handleDropoffSelect}
                    dropLat={dropLat}
                    dropLng={dropLng}
                    isDropoffPinnedLocation={isDropoffPinnedLocation}
                    setIsDropoffPinnedLocation={setIsDropoffPinnedLocation}
                    vendorLat={vendorLat}
                    vendorLong={vendorLong}
                    dropoffInstruction={dropoffInstruction}
                />
                <hr style={{ borderColor: '#344675', borderStyle: 'solid' }}></hr>
                <CardTitle tag="h4" style={{ padding: 5, paddingTop: 10, textAlign: 'left' }}>
                    Shipment Information
                </CardTitle>
                <LineItems
                    title="Description"
                    items={defaultItems}
                    setItems={setDefaultItems}
                    itemsErrorArray={defaultItemsError}
                    setItemsErrorArray={setDefaultItemsError}
                    pickList1={[]}
                    pickList2={[]}
                />
                <MerchantOrderID merchantOrderID={merchantOrderID} setMerchantOrderID={setMerchantOrderID} />
                <BrokerAssistPanel
                    brokerAssist={brokerAssist}
                    brokerLoading={brokerLoading}
                    brokerError={brokerError}
                    onAnalyze={handleBrokerAssist}
                />
                <Row className="justify-content-md-center" style={{ textAlign: 'left', marginBottom: 30, marginLeft: 30 }}>
                    <WightOptions errors={errors} handleWeight={handleWeight} weight={weight} />
                    <Tools
                        setIsForkLift={setIsForkLift}
                        isForkLift={isForkLift}
                        setIsDolly={setIsDolly}
                        isDolly={isDolly}
                        setIsHandLoadUnload={setIsHandLoadUnload}
                        isHandLoadUnload={isHandLoadUnload}
                        setIsHazmat={setIsHazmat}
                        isHazmat={isHazmat}
                        setIsHotBag={setIsHotBag}
                        isHotBag={isHotBag}
                        setIsCooler={setIsCooler}
                        isCooler={isCooler}
                        isTWICCard={isTWICCard}
                        setIsTWICCard={setIsTWICCard}
                        isLiftgate={isLiftgate}
                        setIsLiftgate={setIsLiftgate}
                        isPalletJack={isPalletJack}
                        setIsPalletJack={setIsPalletJack}
                        isWeatherProtection={isWeatherProtection}
                        setIsWeatherProtection={setIsWeatherProtection}
                        isMedical={isMedical}
                        setIsMedical={setIsMedical}
                        isPPE={isPPE}
                        setIsPPE={setIsPPE}
                        weight={weight}
                        vehicleType={vehicleType}
                    />
                </Row>
                <Row className="justify-content-md-center" style={{ textAlign: 'left', marginBottom: 30, marginLeft: 30 }}>
                    <Col md="auto" style={{ marginTop: 30, marginRight: 100 }}>
                        <NumberOfGophrs numPeople={numPeople} setNumPeople={setNumPeople} />
                    </Col>
                    <Col md="auto" style={{ marginTop: 30 }}>
                        <VehicleTypes
                            weight={weight}
                            vehicleType={vehicleType}
                            setVehicleType={handleManualVehicleTypeChange}
                            trailerDimensions={trailerDimensions}
                            setTrailerDimensions={setTrailerDimensions}
                            trailerDimensionsError={errors.trailerDimensions}
                        />
                    </Col>
                </Row>
                <div hidden={errorMessageSubmit.errorCode !== 8}>
                    <p style={{ color: 'red', marginTop: '4px' }}>{errorMessageSubmit.errorMessage}</p>
                </div>
                {priceChoice(standardPrice, expeditedPrice, superExpeditedPrice)}
                {dynamicPricingInfo && (
                    <div style={{
                        margin: '8px 0 4px',
                        padding: '6px 12px',
                        borderRadius: 6,
                        background: dynamicPricingInfo.fulfillment_confidence === 'HIGH' ? '#d4edda'
                            : dynamicPricingInfo.fulfillment_confidence === 'NORMAL' ? '#cce5ff'
                            : dynamicPricingInfo.fulfillment_confidence === 'LIMITED' ? '#fff3cd'
                            : '#f8d7da',
                        color: '#333',
                        fontSize: 12,
                        display: 'flex',
                        gap: 16,
                        alignItems: 'center',
                    }}>
                        <span><strong>Dynamic Pricing</strong></span>
                        <span>Service: <strong>{dynamicPricingInfo.service_level}</strong></span>
                        <span>Confidence: <strong>{dynamicPricingInfo.fulfillment_confidence}</strong></span>
                        {dynamicPricingInfo.expires_at && (
                            <span style={{ color: '#666' }}>
                                Expires: {new Date(dynamicPricingInfo.expires_at).toLocaleTimeString()}
                            </span>
                        )}
                    </div>
                )}
                <div hidden={!twoStopsError}>
                    <p style={{ color: 'red', marginTop: '4px', paddingBottom: 20 }}>Please fix the problem(s) on this form</p>
                </div>
                <Row style={{ paddingLeft: `15px`, marginTop: '25px' }}>
                    <FormGroup>
                        <Button className="btn cancel-btn" color="default" onClick={reset}>
                            Cancel
                        </Button>
                    </FormGroup>
                </Row>
            </Collapse>
        </div>
    );
};

export default RegularCreation;
